import type { IAreaBoundary } from '@/entities/destination'
import type { ICountry, IProvince } from '@/entities/country'
import { useTranslation } from 'react-i18next'
import { ImageUploadField } from '@/shared/ui/image-upload-field'
import { Input } from '@/shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

interface DestinationFormValue {
  countryId: string
  provinceId: string
  slug: string
  destinationType: string
  /** GeoJSON polygon boundary */
  areaBoundary: IAreaBoundary | null
  /** GeoJSON center point [lng, lat] */
  centerPoint: [number, number] | null
  banner: string
  thumbnail: string
  isActive: boolean
}

interface DestinationFormFieldsProps {
  value: DestinationFormValue
  countries: ICountry[]
  provinces: IProvince[]
  onChange: (next: DestinationFormValue) => void
  onPreview?: (url: string) => void
  onBannerUploadingChange?: (isUploading: boolean) => void
  onThumbnailUploadingChange?: (isUploading: boolean) => void
  onUploadSuccess?: () => void
  onUploadError?: (message: string) => void
  readonly?: boolean
}

export function DestinationFormFields({
  value,
  countries,
  provinces,
  onChange,
  onPreview,
  onBannerUploadingChange,
  onThumbnailUploadingChange,
  onUploadSuccess,
  onUploadError,
  readonly = false,
}: DestinationFormFieldsProps) {
  const { t } = useTranslation()
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            {t('destination.country')} <span className="text-red-500">*</span>
          </label>
          <Select
            value={value.countryId}
            onValueChange={(countryId) => onChange({ ...value, countryId, provinceId: '' })}
            disabled={readonly}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('destination.country')} />
            </SelectTrigger>
            <SelectContent>
              {countries.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.nativeName ?? item.name ?? item.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            {t('destination.province')} <span className="text-red-500">*</span>
          </label>
          <Select
            value={value.provinceId}
            onValueChange={(provinceId) => onChange({ ...value, provinceId })}
            disabled={readonly || !value.countryId}
          >
            <SelectTrigger>
              <SelectValue placeholder={t('destination.province')} />
            </SelectTrigger>
            <SelectContent>
              {provinces.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.nativeName ?? item.name ?? item.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            Slug
            <span className="text-red-500">*</span>
          </label>
          <Input
            value={value.slug}
            onChange={(event) => onChange({ ...value, slug: event.target.value })}
            readOnly={readonly}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">
            {t('common.type', { defaultValue: 'Type' })}
          </label>
          <Input
            value={value.destinationType}
            onChange={(event) => onChange({ ...value, destinationType: event.target.value })}
            readOnly={readonly}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ImageUploadField
          label={t('destination.banner')}
          required
          value={value.banner}
          onChange={(banner) => onChange({ ...value, banner })}
          disabled={readonly}
          {...(onUploadSuccess ? { onUploadSuccess } : {})}
          {...(onUploadError ? { onUploadError } : {})}
          {...(onBannerUploadingChange ? { onUploadingChange: onBannerUploadingChange } : {})}
          {...(onPreview ? { onPreview } : {})}
          buttonSize="sm"
          previewWrapperClassName="min-h-0"
          previewButtonClassName="group relative block w-full cursor-zoom-in rounded-lg border border-zinc-200 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2"
          previewImageClassName="pointer-events-none h-full w-full rounded-[inherit] object-cover transition group-hover:opacity-90"
          texts={{
            select: t('tour.selectImage', { defaultValue: 'Select image' }),
            uploading: t('tour.uploadingImage', { defaultValue: 'Uploading...' }),
            empty: '—',
            previewAlt: t('destination.banner'),
          }}
        />
        <ImageUploadField
          label={t('destination.thumbnail')}
          required
          value={value.thumbnail}
          onChange={(thumbnail) => onChange({ ...value, thumbnail })}
          disabled={readonly}
          {...(onUploadSuccess ? { onUploadSuccess } : {})}
          {...(onUploadError ? { onUploadError } : {})}
          {...(onThumbnailUploadingChange ? { onUploadingChange: onThumbnailUploadingChange } : {})}
          {...(onPreview ? { onPreview } : {})}
          buttonSize="sm"
          previewWrapperClassName="min-h-0"
          previewButtonClassName="group relative block w-full cursor-zoom-in rounded-lg border border-zinc-200 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-2"
          previewImageClassName="pointer-events-none h-full w-full rounded-[inherit] object-cover transition group-hover:opacity-90"
          texts={{
            select: t('tour.selectImage', { defaultValue: 'Select image' }),
            uploading: t('tour.uploadingImage', { defaultValue: 'Uploading...' }),
            empty: '—',
            previewAlt: t('destination.thumbnail'),
          }}
        />
      </div>
    </div>
  )
}

export type { DestinationFormValue }
